import { PkButton, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parse from 'html-react-parser';
import i18n, { t } from 'i18next';
import type { CSSProperties } from 'react';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import type { ArticleResult } from 'api/cmsApi';
import { useGetHighlightsFromCMSQuery } from 'api/cmsApi';

import * as style from './style.module.css';

interface MatchHighlightsData {
  isDesktop: boolean;
  matchId: string;
  competitionId: string;
  isPDFPrint?: boolean;
}

const cssPropertiesPrimary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-m)',
  fontFamily: 'var(--pk-font-base-regular)',
  lineHeight: 'var(--pk-font-line-height-l)',
};

export const MatchHighlights = ({ isDesktop, matchId, competitionId, isPDFPrint }: MatchHighlightsData) => {
  const [showAllFact, handleShowAllFact] = useState(false);
  const pkMatchHighlight = useRef<HTMLDivElement>(null);

  const {
    data: MatchHighlightsData,
    error: MatchHighlightsError,
    isLoading: MatchHighlightsLoading,
  } = useGetHighlightsFromCMSQuery({
    matchId,
    competitions: competitionId,
    language: i18n.language,
  });

  const onHandleShowMore = () => {
    setTimeout(() => pkMatchHighlight && pkMatchHighlight.current && pkMatchHighlight.current.scrollIntoView(), 0);
    handleShowAllFact(!showAllFact);
  };

  const renderSection = (section: ArticleResult) => {
    const additionalHorizontalPadding = (): string => {
      switch (section.key) {
        case 'home':
          return isDesktop ? 'pr-px--0 pr-pl--28' : 'pk-pl--l';
        case 'away':
          return isDesktop ? 'pk-pr--xl pr-px--0 pr-pl--28' : 'pk-pl--l';
        case 'main':
          return isDesktop ? 'pk-pr--xl2 pr-px--0 pr-pl--28' : 'pk-pl--l';
        default:
          return '';
      }
    };

    const blurGradientStyle = {
      position: 'absolute',
      top: isDesktop ? '200px' : '280px',
      height: '60px',
      width: '100%',
      'background-color': 'white',
      color: 'white',
      'z-index': 2,
      '-webkit-mask-image': '-webkit-gradient(linear, left top, left bottom, from(rgba(0,0,0,0)), to(rgba(0,0,0,1)))',
    } as CSSProperties;

    const contentArray = section.content?.length && section.content?.split('|').filter((text) => !!text);

    return (
      <div className="pr-page-break--before">
        <div
          className={clsx(
            isDesktop ? 'pk-line-height--md' : 'pk-line-height--sm',
            'pk-font-size--xl pk-font-size-md--xl2 pk-text--text-01 pr-font-size--18 pk-font--comp-bold pk-font-style--normal',
          )}
        >
          {section.title}
        </div>
        <div className={`${additionalHorizontalPadding()}`}>
          <div
            className="pk-m--0 pk-text--05 pr-font-size--14 pr-mb--16"
            style={{
              ...cssPropertiesPrimary,
              maxHeight: isPDFPrint || showAllFact ? '100%' : isDesktop ? '260px' : '340px',
              overflow: isPDFPrint || showAllFact ? 'visible' : 'hidden',
              position: 'relative',
            }}
          >
            {Array.isArray(contentArray) ? (
              contentArray?.map((item: string | undefined) => item && parse(item))
            ) : (
              <div style={{ maxHeight: '260px', width: '300px' }}>No content available yet</div>
            )}
            {!isPDFPrint && !showAllFact && <div style={blurGradientStyle} />}
          </div>
        </div>
      </div>
    );
  };

  const getLoadingComponent = () => {
    return <div className="pk-d--none" />;
  };

  if (MatchHighlightsLoading || MatchHighlightsError) {
    return getLoadingComponent();
  }

  if (MatchHighlightsData && !MatchHighlightsData.main) {
    return <div />;
  }

  return (
    <div
      ref={pkMatchHighlight}
      className={clsx('pk-bg--elevation-01 pk-px--0 pk-px-md--m pk-pt--xs pk-pt-md--xl', style.matchFacts)}
    >
      {MatchHighlightsData?.main && (
        <ListWrapper $isDesktop={isDesktop} className="pk-p--l pk-font-style--normal pk-line-height--md pk-mb--0">
          {renderSection(MatchHighlightsData.main)}
        </ListWrapper>
      )}
      <div className="pk-d-sm--flex">
        {MatchHighlightsData?.home && (
          <ListWrapper
            $isDesktop={isDesktop}
            className="pk-p--l pk-font-style--normal pk-line-height--md pk-mb--0 pr-width--46p"
          >
            {renderSection(MatchHighlightsData.home)}
          </ListWrapper>
        )}
        {MatchHighlightsData?.away && (
          <ListWrapper
            $isDesktop={isDesktop}
            className="pk-p--l pk-font-style--normal pk-line-height--md pk-mb--0 pr-width--46p"
          >
            {renderSection(MatchHighlightsData.away)}
          </ListWrapper>
        )}
      </div>
      {!isPDFPrint && (
        <div className="pk-d--flex pk-flex--column">
          <div className="pk-pb--l pk-px--l pk-align-self--end">
            <PkButton appearance="text" onClick={onHandleShowMore}>
              <span>{t(showAllFact ? 'see_less_facts' : 'see_more_facts')}</span>
              <PkIcon name={showAllFact ? 'navigation-chevron-up' : 'navigation-chevron-down'} />
            </PkButton>
          </div>
        </div>
      )}
    </div>
  );
};

const ListWrapper = styled.div<{ $isDesktop: boolean }>`
  li {
    list-style: disc;
    color: #2c2c2c;
  }
  li:not(:last-child) {
    margin-bottom: ${(props) => (props.$isDesktop ? '40px' : '24px')};
  }
`;
